/**
 * Structure domain logic — pure, framework-free helpers для экрана «Структура»
 * (AK-345) и жизненного цикла полей на карточке (AK-346). Вся бизнес-логика
 * здесь, чтобы покрыть vitest'ом без рендера: валидация реквизитов юрлица,
 * подсчёт каскада архивации, сборка дерева, capability-флаги.
 *
 * Источники правды (backend):
 *   - FarmIn (inn/ogrn/kpp pattern) — backend/src/ikolhoz/schemas/structure.py.
 *     Клиентские regex тут ЗЕРКАЛЯТ pattern из схемы; финальная проверка — на
 *     бэке (422 при несовпадении).
 *   - archive_farm каскад (fields + sowings) — backend/src/ikolhoz/api/v1/farms.py
 *     и fields.py:archive_field.
 *   - «Показать архивные» / restore / name-history — бэк дорабатывается
 *     (AK-344), см. structureCapabilities.
 */

import type {
  ClusterNode,
  CompanyNode,
  FarmNode,
  FarmWithAggregate,
  StructureTree,
} from './types';

// ─── Валидация реквизитов юрлица (зеркало FarmIn pattern) ───────────────────

/** ИНН: 10 цифр (юрлицо) или 12 (ИП). Зеркало `^\d{10}$|^\d{12}$`. */
const INN_RE = /^(\d{10}|\d{12})$/;
/** ОГРН: 13 цифр (юрлицо) или 15 (ОГРНИП). Зеркало `^\d{13}$|^\d{15}$`. */
const OGRN_RE = /^(\d{13}|\d{15})$/;
/** КПП: ровно 9 цифр. Зеркало `^\d{9}$`. */
const KPP_RE = /^\d{9}$/;

export type RequisiteField = 'inn' | 'ogrn' | 'kpp';

/** Результат одной инлайн-проверки: ok + сообщение об ошибке / успехе. */
export interface FieldCheck {
  /** null = поле пустое (нейтрально), true = валидно, false = ошибка. */
  ok: boolean | null;
  message?: string;
}

/**
 * Валидация ИНН. Пусто → нейтрально (поле необязательно даже при юрлице до
 * ввода). 10 цифр → юрлицо, 12 → ИП. Иначе — ошибка с подсказкой сколько введено.
 */
export function checkInn(raw: string): FieldCheck {
  const v = raw.trim();
  if (!v) return { ok: null };
  if (INN_RE.test(v)) {
    return { ok: true, message: v.length === 10 ? '10 цифр — юрлицо' : '12 цифр — ИП' };
  }
  if (!/^\d+$/.test(v)) return { ok: false, message: 'ИНН — только цифры' };
  return { ok: false, message: `ИНН — 10 цифр (юрлицо) или 12 (ИП). Введено ${v.length}.` };
}

/** Валидация ОГРН/ОГРНИП. 13 → юрлицо, 15 → ИП. */
export function checkOgrn(raw: string): FieldCheck {
  const v = raw.trim();
  if (!v) return { ok: null };
  if (OGRN_RE.test(v)) {
    return { ok: true, message: v.length === 13 ? '13 цифр — ОГРН' : '15 цифр — ОГРНИП' };
  }
  if (!/^\d+$/.test(v)) return { ok: false, message: 'ОГРН — только цифры' };
  return { ok: false, message: `ОГРН — 13 цифр (или ОГРНИП — 15). Введено ${v.length}.` };
}

/** Валидация КПП. Ровно 9 цифр. */
export function checkKpp(raw: string): FieldCheck {
  const v = raw.trim();
  if (!v) return { ok: null };
  if (KPP_RE.test(v)) return { ok: true, message: '9 цифр' };
  if (!/^\d+$/.test(v)) return { ok: false, message: 'КПП — только цифры' };
  return { ok: false, message: `КПП — 9 цифр. Введено ${v.length}.` };
}

const REQUISITE_CHECKERS: Record<RequisiteField, (raw: string) => FieldCheck> = {
  inn: checkInn,
  ogrn: checkOgrn,
  kpp: checkKpp,
};

export function checkRequisite(field: RequisiteField, raw: string): FieldCheck {
  return REQUISITE_CHECKERS[field](raw);
}

// ─── Валидация формы хозяйства (FarmIn) ─────────────────────────────────────

export interface FarmFormDraft {
  name: string;
  short_name?: string | null;
  cluster_id?: string | null;
  has_own_legal_entity: boolean;
  inn?: string | null;
  ogrn?: string | null;
  kpp?: string | null;
  director_email?: string | null;
}

export interface FarmFormErrors {
  name?: string;
  inn?: string;
  ogrn?: string;
  kpp?: string;
  director_email?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Валидация формы создания/редактирования хозяйства. Обязательно только
 * название. Реквизиты проверяются только при включённом тумблере «Своё
 * юрлицо» — при выключенном они не обязательны (и очищаются перед отправкой,
 * см. sanitizeFarmPayload). Возвращает объект ошибок (пустой = ок).
 */
export function validateFarmForm(draft: FarmFormDraft): FarmFormErrors {
  const errors: FarmFormErrors = {};

  if (!draft.name.trim()) {
    errors.name = 'Укажите название хозяйства';
  }

  if (draft.has_own_legal_entity) {
    const inn = checkRequisite('inn', draft.inn ?? '');
    if (inn.ok === false) errors.inn = inn.message;
    const ogrn = checkRequisite('ogrn', draft.ogrn ?? '');
    if (ogrn.ok === false) errors.ogrn = ogrn.message;
    const kpp = checkRequisite('kpp', draft.kpp ?? '');
    if (kpp.ok === false) errors.kpp = kpp.message;
  }

  const email = (draft.director_email ?? '').trim();
  if (email && !EMAIL_RE.test(email)) {
    errors.director_email = 'Некорректный email';
  }

  return errors;
}

export function isFarmFormValid(errors: FarmFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

/**
 * Приводит черновик к payload для FarmIn: если «Своё юрлицо» выключено —
 * реквизиты и юр-поля обнуляются (иначе бэк отвалит по pattern на «мусорной»
 * строке). Пустые строки → null (Pydantic принимает null охотнее, чем "").
 */
export function sanitizeFarmPayload<T extends Record<string, unknown>>(
  draft: T & { has_own_legal_entity: boolean },
): T {
  const clean = { ...draft } as Record<string, unknown>;
  const emptyToNull = (k: string) => {
    if (clean[k] === '' || clean[k] === undefined) clean[k] = null;
  };
  for (const k of Object.keys(clean)) emptyToNull(k);

  if (!draft.has_own_legal_entity) {
    for (const k of [
      'legal_entity_name',
      'inn',
      'ogrn',
      'kpp',
      'legal_address',
      'actual_address',
    ]) {
      clean[k] = null;
    }
  }
  return clean as T;
}

// ─── Каскад архивации ───────────────────────────────────────────────────────

export interface CascadeCount {
  fields: number;
  sowings: number;
}

/**
 * Подсчёт «сколько скроется» при архивации хозяйства — для confirm-модалки.
 * fields — активные поля хозяйства; sowings — активные посевы этих полей.
 * Бэк каскадит те же сущности (archive_farm → fields → sowings).
 */
export function cascadeForFarm(farm: FarmWithAggregate): CascadeCount {
  return {
    fields: farm.field_count ?? 0,
    sowings: farm.sowing_count ?? 0,
  };
}

/**
 * Можно ли архивировать это хозяйство. Нельзя заархивировать последнее
 * активное хозяйство компании (иначе структура станет пустой — правило из
 * wireframe). activeFarmCount — общее число активных хозяйств компании.
 */
export function canArchiveFarm(activeFarmCount: number): {
  allowed: boolean;
  reason?: string;
} {
  if (activeFarmCount <= 1) {
    return {
      allowed: false,
      reason: 'В компании должно остаться хотя бы одно активное хозяйство',
    };
  }
  return { allowed: true };
}

// ─── Передача поля ──────────────────────────────────────────────────────────

export interface TransferDraft {
  toFarmId: string | null;
  transferredOn: string;
  /** id хозяйства-владельца сейчас — нельзя передать в него же. */
  fromFarmId: string;
}

export interface TransferValidation {
  ok: boolean;
  farmError?: string;
  dateError?: string;
}

/**
 * Валидация передачи поля. Приёмник обязателен, не равен текущему, дата
 * обязательна. Передача в архивное хозяйство блокируется на уровне выбора
 * (archived не попадают в список опций — см. transferTargetFarms), но на
 * всякий случай проверяем и здесь по флагу.
 */
export function validateTransfer(
  draft: TransferDraft,
  targetIsArchived = false,
): TransferValidation {
  const result: TransferValidation = { ok: true };
  if (!draft.toFarmId) {
    result.ok = false;
    result.farmError = 'Выберите хозяйство-приёмник';
  } else if (draft.toFarmId === draft.fromFarmId) {
    result.ok = false;
    result.farmError = 'Это то же хозяйство — передача не нужна';
  } else if (targetIsArchived) {
    result.ok = false;
    result.farmError = 'Нельзя передать поле в архивное хозяйство';
  }
  if (!draft.transferredOn) {
    result.ok = false;
    result.dateError = 'Укажите дату передачи';
  }
  return result;
}

/**
 * Список хозяйств-приёмников для передачи поля: только активные и не текущее.
 * Архивные исключены (краевой случай из wireframe).
 */
export function transferTargetFarms<T extends { id: string; archived?: boolean }>(
  farms: readonly T[],
  currentFarmId: string,
): T[] {
  return farms.filter((f) => f.id !== currentFarmId && !f.archived);
}

// ─── Сборка дерева структуры ────────────────────────────────────────────────

/**
 * Строит дерево компания → кластер → хозяйство из плоских списков. Хозяйства
 * без cluster_id собираются в псевдо-группу «Вне кластера». Агрегаты (поля/га)
 * по кластеру и компании суммируются из хозяйств.
 *
 * `search` (если задан) фильтрует хозяйства и кластеры по подстроке имени
 * (case-insensitive); пустые кластеры после фильтра скрываются.
 */
export function buildStructureTree(
  companies: readonly { id: string; name: string }[],
  clusters: readonly { id: string; name: string; company_id: string }[],
  farms: readonly FarmWithAggregate[],
  search = '',
): StructureTree {
  const q = search.trim().toLowerCase();
  const matches = (name: string) => !q || name.toLowerCase().includes(q);

  const company = companies[0] ?? null;

  // Индекс кластеров.
  const clusterById = new Map(clusters.map((c) => [c.id, c]));

  const clusterNodes = new Map<string, ClusterNode>();
  const noCluster: FarmNode[] = [];

  for (const farm of farms) {
    const farmNode: FarmNode = {
      id: farm.id,
      name: farm.name,
      cluster_id: farm.cluster_id ?? null,
      field_count: farm.field_count ?? 0,
      area_ha: farm.area_ha ?? 0,
      archived: farm.archived ?? false,
    };

    const cid = farm.cluster_id;
    if (cid && clusterById.has(cid)) {
      let node = clusterNodes.get(cid);
      if (!node) {
        const c = clusterById.get(cid)!;
        node = {
          id: c.id,
          name: c.name,
          farms: [],
          field_count: 0,
          area_ha: 0,
          farm_count: 0,
        };
        clusterNodes.set(cid, node);
      }
      node.farms.push(farmNode);
    } else {
      noCluster.push(farmNode);
    }
  }

  // Убедимся, что пустые кластеры (без хозяйств) тоже присутствуют в дереве.
  for (const c of clusters) {
    if (!clusterNodes.has(c.id)) {
      clusterNodes.set(c.id, {
        id: c.id,
        name: c.name,
        farms: [],
        field_count: 0,
        area_ha: 0,
        farm_count: 0,
      });
    }
  }

  // Агрегаты по кластеру.
  const clusterList: ClusterNode[] = [];
  for (const node of clusterNodes.values()) {
    node.farm_count = node.farms.length;
    node.field_count = node.farms.reduce((s, f) => s + f.field_count, 0);
    node.area_ha = node.farms.reduce((s, f) => s + f.area_ha, 0);
    clusterList.push(node);
  }

  // Фильтр по поиску: кластер проходит, если совпадает его имя ИЛИ есть
  // совпадающие хозяйства. Хозяйства фильтруются по имени.
  const filteredClusters = clusterList
    .map((c) => {
      if (matches(c.name)) return c; // весь кластер (со всеми хозяйствами)
      const farms = c.farms.filter((f) => matches(f.name));
      if (farms.length === 0) return null;
      return {
        ...c,
        farms,
        farm_count: farms.length,
        field_count: farms.reduce((s, f) => s + f.field_count, 0),
        area_ha: farms.reduce((s, f) => s + f.area_ha, 0),
      };
    })
    .filter((c): c is ClusterNode => c !== null)
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  const filteredNoCluster = noCluster
    .filter((f) => matches(f.name))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  // Агрегаты по компании — по всем хозяйствам (без учёта фильтра, «истина»).
  const companyNode: CompanyNode | null = company
    ? {
        id: company.id,
        name: company.name,
        cluster_count: clusterList.length,
        farm_count: farms.length,
        field_count: farms.reduce((s, f) => s + (f.field_count ?? 0), 0),
        area_ha: farms.reduce((s, f) => s + (f.area_ha ?? 0), 0),
      }
    : null;

  return {
    company: companyNode,
    clusters: filteredClusters,
    noCluster: filteredNoCluster,
  };
}

// ─── Форматирование ─────────────────────────────────────────────────────────

/** Разделитель тысяч пробелом: 48900 → «48 900». Для га в дереве. */
export function formatHa(ha: number): string {
  return Math.round(ha)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** RU-плюрализация «поле / поля / полей». */
export function pluralFields(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'поле';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'поля';
  return 'полей';
}

/** RU-плюрализация «хозяйство / хозяйства / хозяйств». */
export function pluralFarms(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'хозяйство';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'хозяйства';
  return 'хозяйств';
}

// ─── Capabilities ───────────────────────────────────────────────────────────

/**
 * Флаги «что реально умеет бэк» на 2026-07-03. Жизненный цикл структуры
 * доведён до конца (AK-344 #389 + AK-347 #393): восстановление из архива
 * (farm + field), история имён поля (event sourcing), «действует с» при
 * переименовании, показ архивных в списках через include_archived. Флаги
 * оставлены как runtime-контракт — если появится не-готовая capability,
 * рисуем кнопку с подсказкой «скоро» (CAPABILITY_SOON_HINT).
 *
 * Контракт с head-backend (реализованные эндпоинты):
 *   - POST /farms/{id}/restore  { restore_fields: bool }  → FarmOut
 *   - POST /fields/{id}/restore { restore_sowings: bool } → FieldOut
 *   - GET  /fields/{id}/name-history
 *   - GET  /farms?include_archived=true  (архивные + archived_at/archive_reason)
 *   - GET  /fields?include_archived=true (архивные + archived_at/archive_reason)
 */
export const structureCapabilities = {
  createCluster: true,
  createFarm: true,
  editFarm: true, // PATCH /farms/{id}
  moveFarmCluster: true, // PATCH /farms/{id} с новым cluster_id
  archiveFarm: true, // DELETE /farms/{id} (soft + каскад)
  restoreFarm: true, // POST /farms/{id}/restore {restore_fields} (#393)
  showArchivedFarms: true, // GET /farms?include_archived=true (#393)
  transferField: true, // POST /fields/{id}/transfers
  transferHistory: true, // GET /fields/{id}/transfers
  // Partial PATCH починен в AK-347 (#391): PATCH {name} больше не трёт
  // geometry; name-history + «действует с» (name_valid_from) — AK-344 (#389).
  renameField: true,
  renameFieldEffectiveDate: true,
  fieldNameHistory: true, // GET /fields/{id}/name-history (#389)
  archiveField: true, // DELETE /fields/{id}
  restoreField: true, // POST /fields/{id}/restore {restore_sowings} (#393)
} as const;

export type StructureCapability = keyof typeof structureCapabilities;

export const CAPABILITY_SOON_HINT = 'Скоро — бэкенд дорабатывается';

export function isCapable(cap: StructureCapability): boolean {
  return structureCapabilities[cap];
}
